import { FC } from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  DAILY_NAV_THRESHOLD_KEYS,
  dailyNavDateTimeLabel,
  fractionToPercent,
  getDailyNavThresholdGroups,
  tradingMarketLabel,
} from 'common/constants/dailyNav';
import { DAILY_NAV_RULES_ICON, DAILY_NAV_TOOLTIP_SLOT_PROPS } from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { FirmRulesIconProps } from './FirmRulesIcon.props';

// The firm rules for the displayed day, on hover rather than as a standing strip:
// reference material, not something to act on. Anything actionable stays an alert.
// Same glyph as the row icons, which say where a company departs from these.
const FirmRulesIcon: FC<FirmRulesIconProps> = ({ config, settingsUrl }) => {
  if (!config) {
    return null;
  }

  const thresholdGroups = getDailyNavThresholdGroups(config.short_window_sessions, config.long_window_sessions);

  return (
    <Tooltip
      describeChild
      arrow
      placement="bottom-start"
      slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
      title={
        <Box>
          <Typography variant="caption" fontWeight="bold" display="block">
            Firm rules in effect since {dailyNavDateTimeLabel(config.firm_config_effective_from)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.7 }} fontWeight="bold">
            MARKET
          </Typography>
          <Typography variant="caption" display="block">
            {tradingMarketLabel(config.trading_calendar)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.7 }} fontWeight="bold">
            WINDOWS
          </Typography>
          <Typography variant="caption" display="block">
            {config.short_window_sessions} / {config.long_window_sessions} trading sessions
          </Typography>
          {thresholdGroups.map(group => (
            <Box key={group.title}>
              <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.7 }} fontWeight="bold">
                {group.title.toUpperCase()} DEFAULTS
              </Typography>
              {group.fields
                .filter(field => DAILY_NAV_THRESHOLD_KEYS.includes(field.key))
                .map(field => {
                  const percent = fractionToPercent(config.firm_thresholds?.[field.key]);
                  return (
                    <Typography key={field.key} variant="caption" display="block">
                      {field.label}: <strong>{percent === '' ? '—' : `${percent}%`}</strong>
                    </Typography>
                  );
                })}
            </Box>
          ))}
          {/* So a row's differing numbers do not read as a contradiction. */}
          <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.7 }}>
            Individual companies may override these; each row&apos;s icon says which do.
          </Typography>
          {settingsUrl && (
            <Typography variant="caption" display="block" sx={{ mt: 0.75, opacity: 0.7 }}>
              Click to open Daily NAV settings.
            </Typography>
          )}
        </Box>
      }>
      <Link
        component={settingsUrl ? RouterLink : 'span'}
        to={settingsUrl}
        aria-label={
          settingsUrl
            ? 'Firm rules in effect for this NAV day — open Daily NAV settings'
            : 'Firm rules in effect for this NAV day'
        }
        data-testid="daily-nav-firm-rules-icon"
        sx={{ display: 'inline-flex', color: 'action.active', cursor: settingsUrl ? 'pointer' : 'help' }}>
        <DAILY_NAV_RULES_ICON fontSize="small" />
      </Link>
    </Tooltip>
  );
};

export default FirmRulesIcon;
